<template>
  <!--
    DrawingInspector
    -------------------------------------------------------------------------
    Painel de propriedades da entidade selecionada (peça, elemento, anotação
    ou imagem). É puramente apresentacional: lê o objeto selecionado e emite
    patches. Quem persiste é o StoneTechnicalDrawing2D (e, no fim, o modal).
  -->
  <div class="std-inspector">
    <div v-if="!selected" class="std-inspector-empty">
      <v-icon size="28" color="#9fb3ab">mdi-gesture-tap</v-icon>
      <span class="fonteMini grey--text">Selecione algo no desenho para editar.</span>
    </div>

    <!-- PEÇA de pedra -->
    <template v-else-if="selection.kind === 'piece' && piece">
      <div class="std-inspector-title">{{ pieceTitle }}</div>
      <div class="std-inspector-sub">
        {{ formatMeasure(piece.width) }}m x {{ formatMeasure(piece.height) }}m ·
        {{ piece.billingUnit === 'M2' ? 'M²' : piece.billingUnit }}
      </div>

      <template v-if="piece.isStone">
        <!-- acabamentos vêm do CADASTRO (Insumos > Acabamento): arraste o
             chip da paleta até a borda, ou clique na borda para escolher -->
        <div class="std-finish-hint mt-3">
          <v-icon size="15" color="#5a6660">mdi-drag-variant</v-icon>
          <span>Arraste um acabamento da paleta até a borda da peça.</span>
        </div>
        <div v-if="appliedFinishes.length" class="std-field-label mt-3">Acabamentos aplicados</div>
        <div
          v-for="finish in appliedFinishes"
          :key="`insp-fin-${finish.edge}`"
          class="std-applied-finish"
        >
          <img v-if="finish.imagem" :src="finish.imagem" class="std-applied-thumb" alt="" />
          <span>{{ edgeLabelOf(finish.edge) }}: <strong>{{ finish.nome }}</strong></span>
        </div>

        <!-- cantos arredondados UNILATERAIS (padrão = todos retos) -->
        <template v-if="!piece.shape">
          <div class="std-field-label mt-3">Cantos arredondados (cm)</div>
          <div class="std-corner-grid">
            <v-text-field
              v-for="corner in cornerOptions"
              :key="`corner-${corner.key}`"
              dense
              outlined
              hide-details
              type="number"
              :label="corner.label"
              :value="cornerCm(corner.key)"
              @change="setCorner(corner.key, $event)"
            ></v-text-field>
          </div>
        </template>

        <!-- peça secundária/vinculada: relação com a principal (perspectiva) -->
        <template v-if="piece.isSecondary || piece.linkedTo">
          <div class="std-field-label mt-3">Relação com a peça principal</div>
          <v-select
            dense
            outlined
            hide-details
            :items="relacaoOptions"
            item-text="label"
            item-value="value"
            :value="piece.relacaoPrincipal || 'neutra'"
            @change="emitPiecePatch({ relacaoPrincipal: $event })"
          ></v-select>
        </template>

        <!-- girar a peça em passos de 90° (saias laterais, peças em pé...) -->
        <v-btn
          text
          x-small
          color="teal darken-1"
          class="mt-3"
          @click="emitPiecePatch({ rotation: (((piece.rotation || 0) + 90) % 360) })"
        >
          <v-icon left size="16">mdi-rotate-right</v-icon>
          Girar 90° ({{ piece.rotation || 0 }}°)
        </v-btn>

        <!-- recortes da borracha: opção de restaurar o retângulo original -->
        <v-btn
          v-if="piece.shape"
          text
          x-small
          color="teal darken-1"
          class="mt-1"
          @click="emitPiecePatch({ shape: null })"
        >
          <v-icon left size="16">mdi-shape-rectangle-plus</v-icon>
          Restaurar formato retangular
        </v-btn>

        <div class="std-field-label mt-3">Chamada da frente (saia/rodapé)</div>
        <v-text-field
          dense
          outlined
          hide-details
          placeholder="ex.: RODA BASE EM PEDRA"
          :value="(piece.edgeLabels || {}).front"
          @change="emitPiecePatch({ edgeLabels: { ...(piece.edgeLabels || {}), front: $event } })"
        ></v-text-field>

        <!-- remove a peça da ficha (junto com saias/acabamentos vinculados) -->
        <v-btn text x-small color="red" class="mt-3" @click="$emit('remove')">
          <v-icon left size="16">mdi-delete</v-icon> Remover peça da ficha
        </v-btn>
      </template>

      <!-- Acabamento como ITEM da ficha (type === Acabamento) -->
      <div v-else-if="piece.isFinishing" class="std-finish-note mt-3">
        <v-icon size="18" color="#075e54">mdi-brush-variant</v-icon>
        <span class="fonteMini">Acabamento é item separado da ficha (vai para a legenda).</span>
      </div>
    </template>

    <!-- RECORTE vinculado (cuba/cooktop/furo) -->
    <template v-else-if="selection.kind === 'cutout' && cutout">
      <div class="std-inspector-title">{{ cutout.label || "Recorte" }}</div>
      <div class="std-inspector-sub">Recorte vinculado à peça</div>
      <v-text-field
        class="mt-3"
        dense outlined hide-details label="Rótulo (chamada)"
        :value="cutout.label"
        @input="emitCutoutPatch({ label: $event })"
      ></v-text-field>
      <template v-if="cutout.diameter">
        <v-text-field
          class="mt-2"
          dense outlined hide-details type="number" :label="`Diâmetro (${cutoutUnit})`"
          :value="toCutoutUnit(cutout.diameter)"
          @change="emitCutoutPatch({ diameter: fromCutoutUnit($event, cutout.diameter) })"
        ></v-text-field>
      </template>
      <div v-else class="std-two-cols mt-2">
        <v-text-field dense outlined hide-details type="number" :label="`Largura (${cutoutUnit})`"
          :value="toCutoutUnit(cutout.width)"
          @change="emitCutoutPatch({ width: fromCutoutUnit($event, cutout.width) })"></v-text-field>
        <v-text-field dense outlined hide-details type="number" :label="`Altura (${cutoutUnit})`"
          :value="toCutoutUnit(cutout.height)"
          @change="emitCutoutPatch({ height: fromCutoutUnit($event, cutout.height) })"></v-text-field>
      </div>
      <div class="std-two-cols mt-2">
        <v-text-field dense outlined hide-details type="number" :label="`Eixo X (${cutoutUnit})`"
          :value="toCutoutUnit(cutout.cx)"
          @change="emitCutoutPatch({ cx: fromCutoutUnit($event, cutout.cx) })"></v-text-field>
        <v-text-field dense outlined hide-details type="number" :label="`Eixo Y (${cutoutUnit})`"
          :value="toCutoutUnit(cutout.cy)"
          @change="emitCutoutPatch({ cy: fromCutoutUnit($event, cutout.cy) })"></v-text-field>
      </div>
      <v-btn text x-small color="red" class="mt-3" @click="$emit('remove')">
        <v-icon left size="16">mdi-delete</v-icon> Remover recorte
      </v-btn>
    </template>

    <!-- ELEMENTO técnico -->
    <template v-else-if="selection.kind === 'element' && element">
      <div class="std-inspector-title">{{ element.label }}</div>
      <div class="std-inspector-sub">Elemento técnico ({{ element.type }})</div>
      <v-text-field
        class="mt-3"
        dense outlined hide-details label="Rótulo"
        :value="element.label"
        @input="emitElementPatch({ label: $event })"
      ></v-text-field>
      <div v-if="element.shape !== 'bezier'" class="std-two-cols mt-2">
        <v-text-field dense outlined hide-details type="number" label="Largura"
          :value="Math.round(element.width)"
          @input="emitElementPatch({ width: toNum($event) })"></v-text-field>
        <v-text-field dense outlined hide-details type="number" label="Altura"
          :value="Math.round(element.height)"
          @input="emitElementPatch({ height: toNum($event) })"></v-text-field>
      </div>
      <!-- parede (bezier): formato se edita arrastando no próprio desenho -->
      <div v-else class="std-finish-hint mt-3">
        <v-icon size="15" color="#5a6660">mdi-vector-point-edit</v-icon>
        <span>Arraste os pontos e as alças de curvatura no desenho para ajustar o formato.</span>
      </div>
      <v-btn text x-small color="red" class="mt-3" @click="$emit('remove')">
        <v-icon left size="16">mdi-delete</v-icon> Remover elemento
      </v-btn>
    </template>

    <!-- ANOTAÇÃO -->
    <template v-else-if="selection.kind === 'annotation' && annotation">
      <div class="std-inspector-title">Anotação</div>
      <v-textarea
        class="mt-2" dense outlined hide-details auto-grow rows="2" label="Texto"
        :value="annotation.text"
        @input="emitAnnotationPatch({ text: $event })"></v-textarea>
      <div class="std-two-cols mt-2">
        <v-text-field dense outlined hide-details type="number" label="Fonte"
          :value="annotation.fontSize"
          @input="emitAnnotationPatch({ fontSize: toNum($event) || 12 })"></v-text-field>
        <v-text-field dense outlined hide-details type="number" label="Rotação"
          :value="annotation.rotation"
          @input="emitAnnotationPatch({ rotation: toNum($event) })"></v-text-field>
      </div>
      <!-- linha de chamada (líder) apontando para um ponto do desenho -->
      <v-btn
        text
        x-small
        :color="annotation.leader ? 'orange darken-2' : 'teal darken-1'"
        class="mt-2"
        @click="toggleLeader"
      >
        <v-icon left size="16">
          {{ annotation.leader ? "mdi-vector-polyline-remove" : "mdi-vector-polyline-plus" }}
        </v-icon>
        {{ annotation.leader ? "Remover linha de chamada" : "Adicionar linha de chamada" }}
      </v-btn>
      <v-btn text x-small color="red" class="mt-1" @click="$emit('remove')">
        <v-icon left size="16">mdi-delete</v-icon> Remover anotação
      </v-btn>
    </template>

    <!-- IMAGEM -->
    <template v-else-if="selection.kind === 'image' && image">
      <div class="std-inspector-title">{{ image.label || 'Imagem' }}</div>
      <v-text-field
        class="mt-2" dense outlined hide-details label="Rótulo"
        :value="image.label"
        @input="emitImagePatch({ label: $event })"></v-text-field>
      <div class="std-field-label mt-3">Opacidade</div>
      <v-slider
        dense hide-details min="0.1" max="1" step="0.05"
        :value="image.opacity"
        @input="emitImagePatch({ opacity: $event })"></v-slider>
      <v-checkbox
        dense hide-details label="Travar imagem"
        :input-value="image.locked"
        @change="emitImagePatch({ locked: !!$event })"></v-checkbox>
      <v-btn text x-small color="red" class="mt-2" @click="$emit('remove')">
        <v-icon left size="16">mdi-delete</v-icon> Remover imagem
      </v-btn>
    </template>
  </div>
</template>

<script>
import { formatMeasure } from "./engine/drawing/measurementUtils";

export default {
  name: "DrawingInspector",
  props: {
    selection: { type: Object, default: () => ({ kind: null, id: null }) },
    piece: { type: Object, default: null },
    element: { type: Object, default: null },
    annotation: { type: Object, default: null },
    image: { type: Object, default: null },
    cutout: { type: Object, default: null }
  },
  data() {
    return {
      edgeLabels: {
        front: "Frente",
        back: "Fundo",
        left: "Esquerda",
        right: "Direita"
      },
      cornerOptions: [
        { key: "tl", label: "Fundo esq." },
        { key: "tr", label: "Fundo dir." },
        { key: "bl", label: "Frente esq." },
        { key: "br", label: "Frente dir." }
      ],
      relacaoOptions: [
        { value: "neutra", label: "Neutra (mesmo plano)" },
        { value: "curva_baixo", label: "Curvada para baixo (saia)" },
        { value: "curva_cima", label: "Curvada para cima (frontão)" },
        { value: "sobreposta_acima", label: "Sobreposta acima" },
        { value: "sobreposta_abaixo", label: "Sobreposta abaixo" }
      ]
    };
  },
  computed: {
    // acabamentos CADASTRADOS aplicados às bordas da peça selecionada
    appliedFinishes() {
      const items = (this.piece && this.piece.edgeFinishItems) || {};
      return Object.keys(items)
        .filter(edge => items[edge] && items[edge].nome)
        .map(edge => ({ edge, ...items[edge] }));
    },
    selected() {
      return this.piece || this.element || this.annotation || this.image || this.cutout;
    },
    cutoutUnit() {
      return this.cutout && this.cutout.displayUnit === "mm" ? "mm" : "cm";
    },
    pieceTitle() {
      if (!this.piece) return "";
      const prefix = this.piece.isPrimary ? "P" : this.piece.isSecondary ? "S" : "AC";
      return `${prefix}${this.piece.sourceIndex + 1} - ${this.piece.description || "Peça"}`;
    }
  },
  methods: {
    formatMeasure,
    toNum(v) {
      return parseFloat(`${v}`.replace(",", ".")) || 0;
    },
    // recortes editam em CM na UI, mas persistem em METROS
    toCutoutUnit(meters) {
      const factor = this.cutoutUnit === "mm" ? 1000 : 100;
      return Number(((Number(meters) || 0) * factor).toFixed(2));
    },
    fromCutoutUnit(value, fallbackMeters) {
      const amount = this.toNum(value);
      const factor = this.cutoutUnit === "mm" ? 1000 : 100;
      return amount > 0 ? amount / factor : fallbackMeters;
    },
    emitCutoutPatch(patch) {
      this.$emit("update-cutout", patch);
    },
    toggleLeader() {
      if (!this.annotation) return;
      const leader = this.annotation.leader
        ? null
        : { x: this.annotation.x + 60, y: this.annotation.y + 40 };
      this.emitAnnotationPatch({ leader });
    },
    edgeLabelOf(edge) {
      return this.edgeLabels[edge] || edge;
    },
    // cantos arredondados: UI em cm, persistência em metros
    cornerCm(key) {
      const radii = (this.piece && this.piece.cornerRadius) || {};
      return Math.round((Number(radii[key]) || 0) * 100);
    },
    setCorner(key, value) {
      const cm = this.toNum(value);
      const radii = { ...((this.piece && this.piece.cornerRadius) || {}) };
      radii[key] = cm > 0 ? cm / 100 : 0;
      const hasAny = Object.values(radii).some(v => Number(v) > 0);
      this.emitPiecePatch({ cornerRadius: hasAny ? radii : null });
    },
    emitPiecePatch(patch) {
      this.$emit("update-piece", patch);
    },
    emitElementPatch(patch) {
      this.$emit("update-element", patch);
    },
    emitAnnotationPatch(patch) {
      this.$emit("update-annotation", patch);
    },
    emitImagePatch(patch) {
      this.$emit("update-image", patch);
    }
  }
};
</script>

<style scoped>
.std-inspector {
  padding: 4px;
}
.std-inspector-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
  padding: 18px 8px;
}
.std-inspector-title {
  font-weight: 700;
  font-size: 13px;
  color: #173b33;
}
.std-inspector-sub {
  font-size: 11px;
  color: #667781;
}
.std-field-label {
  font-size: 11px;
  font-weight: 600;
  color: #667781;
  margin-bottom: 4px;
}
.std-two-cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}
.std-corner-grid {
  display: grid;
  gap: 6px;
  grid-template-columns: 1fr 1fr;
}
.std-finish-hint {
  align-items: center;
  background: #f5f7f6;
  border: 1px solid rgba(7, 94, 84, 0.1);
  border-radius: 8px;
  color: #5a6660;
  display: flex;
  font-size: 11px;
  gap: 6px;
  padding: 6px 8px;
}
.std-applied-finish {
  align-items: center;
  color: #37413d;
  display: flex;
  font-size: 11px;
  gap: 6px;
  margin-top: 3px;
}
.std-applied-thumb {
  border: 1px solid rgba(7, 94, 84, 0.16);
  border-radius: 3px;
  height: 18px;
  object-fit: cover;
  width: 18px;
}
.std-finish-note {
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 10px;
  padding: 8px;
  background: #f3f8f5;
  border: 1px solid rgba(7, 94, 84, 0.1);
}
</style>
